import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, BRL, formatApiErrorDetail, authFileUrl, authFileDownloadUrl } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Paperclip, FileCheck2, Download } from "lucide-react";
import { Progress } from "@/components/ui/progress";

export default function PaymentsDialog({ open, onOpenChange, expense, onSaved }) {
  const [amount, setAmount] = useState("");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(expense);
  const [receipt, setReceipt] = useState({ path: null, name: null });
  const [uploading, setUploading] = useState(false);

  const exp = current || expense;
  const value = exp?.value || 0;
  const paid = exp?.paid || 0;
  const remaining = exp?.remaining ?? Math.max(value - paid, 0);
  const pct = value > 0 ? Math.min(100, Math.round((paid / value) * 100)) : 0;

  const refresh = (updated) => { setCurrent(updated); onSaved && onSaved(); };

  const uploadReceipt = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReceipt({ path: data.path, name: data.filename });
      toast.success("Comprovante anexado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const add = async () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) return toast.error("Informe um valor válido");
    setBusy(true);
    try {
      const { data } = await api.post(`/expenses/${exp.id}/payments`, { amount: val, date: payDate, attachment_path: receipt.path, attachment_name: receipt.name });
      refresh(data);
      setAmount("");
      setReceipt({ path: null, name: null });
      toast.success("Pagamento registrado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const removeAt = async (idx) => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/expenses/${exp.id}/payments/${idx}`);
      refresh(data);
      toast.success("Pagamento removido");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const payments = exp?.payments || [];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setCurrent(null); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-lg" data-testid="payments-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-2xl tracking-tight">Pagamentos — {exp?.name}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3 py-2">
          <div className="rounded-md border border-border/60 p-3">
            <p className="text-xs text-muted-foreground">Valor</p>
            <p className="font-head font-bold tnum">{BRL(value)}</p>
          </div>
          <div className="rounded-md border border-border/60 p-3">
            <p className="text-xs text-muted-foreground">Pago</p>
            <p className="font-head font-bold tnum text-primary" data-testid="payments-paid">{BRL(paid)}</p>
          </div>
          <div className="rounded-md border border-border/60 p-3">
            <p className="text-xs text-muted-foreground">Restante</p>
            <p className="font-head font-bold tnum text-amber-600 dark:text-amber-500" data-testid="payments-remaining">{BRL(remaining)}</p>
          </div>
        </div>
        <Progress value={pct} className="h-2" />

        <div className="flex flex-col sm:flex-row gap-3 sm:items-end mt-2">
          <div className="flex-1 space-y-2">
            <Label>Valor pago</Label>
            <Input type="number" step="0.01" data-testid="payment-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0,00" className="tnum" />
          </div>
          <div className="space-y-2">
            <Label>Data</Label>
            <Input type="date" data-testid="payment-date-input" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
          </div>
          <Button onClick={add} disabled={busy || uploading} data-testid="add-payment-button" className="font-semibold gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Registrar
          </Button>
        </div>

        <div className="space-y-2 mt-1">
          <Label>Comprovante (imagem ou PDF)</Label>
          <label htmlFor="receipt" className="flex items-center gap-2 px-3 py-2.5 rounded-md border border-dashed border-border cursor-pointer hover:bg-accent transition-colors text-sm">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : receipt.name ? <FileCheck2 className="h-4 w-4 text-primary" /> : <Paperclip className="h-4 w-4 text-muted-foreground" />}
            <span className="truncate">{receipt.name || "Anexar comprovante ao pagamento"}</span>
          </label>
          <input id="receipt" type="file" accept="image/*,application/pdf" className="hidden" data-testid="payment-receipt-input" onChange={(e) => uploadReceipt(e.target.files?.[0])} />
        </div>

        <div className="mt-2 max-h-52 overflow-auto divide-y divide-border/60">
          {payments.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Nenhum pagamento registrado ainda.</p>
          ) : (
            payments.map((p, i) => (
              <div key={i} className="flex items-center justify-between py-2.5" data-testid={`payment-item-${i}`}>
                <div className="flex items-center gap-3">
                  <span className="font-semibold tnum">{BRL(p.amount)}</span>
                  <span className="text-xs text-muted-foreground">{new Date(p.date).toLocaleDateString("pt-BR")}</span>
                  {p.attachment_path && (
                    <>
                      <a href={authFileUrl(p.attachment_path)} target="_blank" rel="noreferrer" title={`Ver ${p.attachment_name}`} data-testid={`payment-receipt-link-${i}`}>
                        <Paperclip className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                      </a>
                      <a href={authFileDownloadUrl(p.attachment_path)} title={`Baixar ${p.attachment_name}`} data-testid={`payment-receipt-download-${i}`}>
                        <Download className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                      </a>
                    </>
                  )}
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => removeAt(i)} disabled={busy} data-testid={`remove-payment-${i}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
